/**
 * Phase 8.3 — Non-bay hold time must not inflate rollover bay occupancy.
 *
 * Doctrine:
 *   - Bucket 3 §3.3.3  Held-in-Parking is an active production
 *                      concern but NOT bay occupancy.
 *   - Bucket 3 §3.3.3  A ticket held in parking must NOT block a
 *                      physical bay, must NOT grow next-day bay
 *                      occupancy merely because time passed while
 *                      held outside the bay, and must NOT consume
 *                      bay capacity unless it is actually in a bay,
 *                      specialty bay, or explicitly bay-held.
 *   - Bucket 4.5 §4.5.5  Due Out risk MAY still increase while held
 *                      in parking — promise / breach escalation is
 *                      independent of bay occupancy.
 *   - Bucket 3 §3.4.11 `parking_lot_inactive` (no active dispatch
 *                      plan) is NOT active rollover work.
 *
 * Why this module exists
 * ----------------------
 * Read-first survey at Phase 8.3 confirmed the runtime already
 * implements the doctrine correctly:
 *
 *   - `src/lib/tickets/lifecycle/bayOccupancyEstimate.ts` (Slice
 *     5D-11 Batch 1) gates advisor delay minutes via
 *     `detectVehicleBayPresence` — advisor minutes are NOT counted
 *     as bay occupancy unless the vehicle is `in_bay`. Vehicles in
 *     parking lot OR with unknown presence are explicitly excluded
 *     ("Vehicle in parking lot — bay hold N/A").
 *   - `supabase/functions/_shared/eodRolloverHandler.ts:createEODRolloverSplit`
 *     creates Part B with `predicted_minutes: 1` and `opti_time: 1`
 *     (the "1-minute shadow — grows via durable accumulation"
 *     pattern). The handler does NOT inflate Part B's predicted
 *     minutes from Part A's hold time.
 *   - Phase 1.1 `getProductionState` marks `held_in_parking` as
 *     production-relevant.
 *   - Phase 5.4 `getDueOutRiskExclusionReason` returns `null` for
 *     `held_in_parking` — Due Out risk stays active.
 *   - Phase 2.1 `BayHoldOwner` + `isBayBlockingHoldOwner` express
 *     the explicit bay-hold owner exception (e.g., a parts hold
 *     where the bay is doctrinally held even if the vehicle is in
 *     the lot for safety reasons).
 *
 * Phase 8.3 supplies a canonical verdict + blocker helper so any
 * future planner / cascade / UI can ask "does this ticket's hold
 * time inflate rollover bay occupancy?" and get a clear answer
 * without re-deriving the doctrine. Phase 8.3 also adds regression
 * tests + source-scan pins to lock the current runtime behavior.
 *
 * Phase 8.3 scope: HELPER + REGRESSION TESTS ONLY.
 *   - Pure. No DB I/O, no clock reads, no React hooks.
 *   - No DB migration / no schema change.
 *   - No runtime wiring. `bayOccupancyEstimate`, `eodRolloverHandler`,
 *     `createEODRolloverSplit`, cascade, AutoBump, timer state
 *     machine are UNTOUCHED. Source-scan tests pin the existing
 *     correctness.
 *
 * Builds on:
 *   - Phase 1.1 `getProductionState` + Phase 1.3 `isHeldInParking`.
 *   - Phase 5.4 `getDueOutRiskExclusionReason`.
 *   - Phase 2.1 `BayHoldOwner` + `isBayBlockingHoldOwner` for the
 *     explicit bay-hold-owner exception.
 *   - Phase 2.4 `getTimerStartLocationState` for the canonical 4-state
 *     physical location classifier.
 *   - Slice 5D-11 `detectVehicleBayPresence` (display-only but
 *     doctrinally identical).
 */

import {
  getProductionState,
  isHeldInParking,
  type ProductionStateTicketShape,
} from './ticketProductionState';
import {
  getDueOutRiskExclusionReason,
  type DueOutRiskTicketShape,
} from './dueOutRiskEligibility';
import {
  isBayBlockingHoldOwner,
  type BayHoldOwner,
} from './bayHoldOwner';
import {
  getTimerStartLocationState,
} from './parkingToBayTimerStart';
import { isTicketDeleted } from '@/utils/ticketDeletedState';

// =============================================================================
// Vocabulary
// =============================================================================

/**
 * Discrete verdicts the helper emits.
 */
export type RolloverBayOccupancyVerdict =
  | 'no_bay_occupancy_growth'
  | 'bay_occupancy_growth_allowed'
  | 'due_out_risk_growth_only'
  | 'bay_hold_owner_blocks_bay'
  | 'in_bay_pause_blocks_bay'
  | 'held_in_parking_not_bay_blocking'
  | 'parking_lot_inactive_no_active_rollover'
  | 'missing_location_context'
  | 'requires_manual_review';

export const ROLLOVER_BAY_OCCUPANCY_VERDICTS = Object.freeze([
  'no_bay_occupancy_growth',
  'bay_occupancy_growth_allowed',
  'due_out_risk_growth_only',
  'bay_hold_owner_blocks_bay',
  'in_bay_pause_blocks_bay',
  'held_in_parking_not_bay_blocking',
  'parking_lot_inactive_no_active_rollover',
  'missing_location_context',
  'requires_manual_review',
] as const) satisfies ReadonlyArray<RolloverBayOccupancyVerdict>;

/** Discrete blocker / warning codes. */
export type NonBayHoldBlocker =
  | 'held_in_parking_cannot_inflate_bay_occupancy'
  | 'non_bay_hold_not_bay_blocking'
  | 'due_out_risk_may_increase'
  | 'bay_id_required_for_bay_occupancy_growth'
  | 'parking_location_suppresses_bay_occupancy'
  | 'explicit_bay_hold_owner_required'
  | 'parking_lot_inactive_excluded'
  | 'missing_bay_context'
  | 'missing_due_out_context';

export const NON_BAY_HOLD_BLOCKERS = Object.freeze([
  'held_in_parking_cannot_inflate_bay_occupancy',
  'non_bay_hold_not_bay_blocking',
  'due_out_risk_may_increase',
  'bay_id_required_for_bay_occupancy_growth',
  'parking_location_suppresses_bay_occupancy',
  'explicit_bay_hold_owner_required',
  'parking_lot_inactive_excluded',
  'missing_bay_context',
  'missing_due_out_context',
] as const) satisfies ReadonlyArray<NonBayHoldBlocker>;

// =============================================================================
// Input shape
// =============================================================================

/**
 * Permissive ticket shape extending Phase 1.1 + Phase 5.4 shapes.
 * Accepts caller-supplied bay-hold-owner signal for the explicit
 * bay-hold doctrine.
 */
export interface NonBayHoldRolloverShape
  extends ProductionStateTicketShape,
    DueOutRiskTicketShape {
  /** Bay assignment. Canonical signal for bay occupancy growth. */
  bay_id?: string | null;
  /** Specialty bay assignment. Treated as physical bay occupancy. */
  specialty_bay_id?: string | null;
  /** Parking spot id (suppresses bay occupancy). */
  parking_spot_id?: string | null;
  /** General lot flag (suppresses bay occupancy). */
  parking_lot?: boolean | null;
  /** Caller-supplied Phase 2.1 hold owner (when on a pause). */
  bay_hold_owner?: BayHoldOwner | null;
  /** Caller-supplied: explicit bay-hold-owner flag (Phase 2.1). */
  is_explicit_bay_hold?: boolean | null;
}

export interface NonBayHoldRolloverContext {
  /**
   * Requested hold minutes the CALLER wants to attribute to either
   * bay occupancy or Due Out risk. Phase 8.3 routes this to whichever
   * channel the doctrine permits; the helper does NOT compute it.
   */
  requestedHoldMinutes?: number | null;
  /** Caller-supplied explicit override. */
  treatAsExplicitBayHold?: boolean;
}

// =============================================================================
// Verdict
// =============================================================================

export interface NonBayHoldRolloverVerdict {
  verdict: RolloverBayOccupancyVerdict;
  blockers: NonBayHoldBlocker[];
  /** Minutes that may grow bay-blocking rollover occupancy. */
  bayBlockingMinutes: number;
  /** Minutes that may grow Due Out risk (independent of bay). */
  dueOutRiskMinutes: number;
  /** True iff verdict is one of the "safe to grow bay occupancy" states. */
  ok: boolean;
}

// =============================================================================
// Internal helpers
// =============================================================================

function pushUnique<T>(arr: T[], v: T): void {
  if (!arr.includes(v)) arr.push(v);
}

function isTerminalForRollover(t: NonBayHoldRolloverShape): boolean {
  if (t == null) return false;
  if (isTicketDeleted(t) || t.status === 'Cancelled') return true;
  if (t.picked_up_at != null) return true;
  if (t.archived_at != null) return true;
  const state = getProductionState(t);
  return state === 'deleted' || state === 'archived' || state === 'picked_up';
}

function clampPositive(n: unknown): number {
  if (typeof n !== 'number' || !Number.isFinite(n) || n <= 0) return 0;
  return n;
}

// =============================================================================
// Public predicates
// =============================================================================

/**
 * Boolean: should the caller's `requestedHoldMinutes` inflate the
 * rollover bay occupancy for THIS ticket?
 *
 * Returns true ONLY when:
 *   - The ticket has an actual bay assignment (`bay_id` OR
 *     `specialty_bay_id`) AND is NOT physically in parking
 *     (parking_lot=false, no parking_spot_id), OR
 *   - The ticket's `bay_hold_owner` is an explicit bay-blocking
 *     owner per Phase 2.1 (parts / vendor / sublet / machine —
 *     `isBayBlockingHoldOwner` returns true).
 *
 * Returns false for held-in-parking, parking_lot_inactive,
 * scheduled_but_parked, picked_up, deleted, archived, cancelled.
 */
export function shouldHoldTimeInflateRolloverBayOccupancy(
  ticket: NonBayHoldRolloverShape,
  context?: NonBayHoldRolloverContext,
): boolean {
  if (ticket == null) return false;
  if (isTerminalForRollover(ticket)) return false;

  // Explicit caller override (Phase 2.1 doctrine).
  if (context?.treatAsExplicitBayHold === true) return true;

  // Phase 2.1: explicit bay-blocking hold owner (parts / vendor /
  // sublet / machine).
  if (
    ticket.bay_hold_owner != null &&
    isBayBlockingHoldOwner(ticket.bay_hold_owner)
  ) {
    return true;
  }
  if (ticket.is_explicit_bay_hold === true) return true;

  // Phase 2.4 physical-location classifier.
  const loc = getTimerStartLocationState(ticket);
  // Only in_bay can inflate bay occupancy. Specialty bays are also
  // physical (Phase 7.8 default model treats them as physical).
  if (loc === 'in_bay') return true;
  if (ticket.bay_id == null && ticket.specialty_bay_id != null) {
    // Phase 7.8 default — specialty entries are physical unless
    // caller has marked otherwise.
    return true;
  }
  return false;
}

// =============================================================================
// Verdict builder
// =============================================================================

/**
 * Build the canonical rollover-bay-occupancy impact verdict. Pure.
 *
 * Resolution order (terminal-first):
 *
 *   1. Null / undefined ticket → `missing_location_context` +
 *      `missing_bay_context`.
 *   2. Terminal (picked_up / deleted / archived / Cancelled) →
 *      `no_bay_occupancy_growth`.
 *   3. Production state `parking_lot_inactive` →
 *      `parking_lot_inactive_no_active_rollover` +
 *      `parking_lot_inactive_excluded`. No bay, no Due Out growth.
 *   4. Phase 2.1 explicit bay-blocking hold owner (or caller override)
 *      → `bay_hold_owner_blocks_bay` + `bay_occupancy_growth_allowed`.
 *      Bay AND Due Out can grow.
 *   5. Production state `held_in_bay` → `in_bay_pause_blocks_bay` +
 *      `bay_occupancy_growth_allowed`. Bay AND Due Out can grow.
 *   6. Production state `active_in_bay` → `bay_occupancy_growth_allowed`.
 *      Bay AND Due Out can grow.
 *   7. Production state `held_in_parking` or `scheduled_but_parked`
 *      → `held_in_parking_not_bay_blocking` +
 *      `held_in_parking_cannot_inflate_bay_occupancy` +
 *      `non_bay_hold_not_bay_blocking`. Bay growth = 0;
 *      Due Out risk MAY grow.
 *   8. Physical location `in_parking_spot` or `in_general_lot`
 *      (without a bay_id) → `due_out_risk_growth_only` +
 *      `parking_location_suppresses_bay_occupancy`. Bay growth = 0;
 *      Due Out grows (when Due Out risk applies).
 *   9. No location signals + production state `estimate_prospect` →
 *      `no_bay_occupancy_growth` (not yet scheduled work).
 *  10. Otherwise (incomplete context) →
 *      `requires_manual_review` + `missing_bay_context`.
 *
 * The `bayBlockingMinutes` field is the requested minutes when the
 * verdict allows bay growth, otherwise 0.
 * The `dueOutRiskMinutes` field is the requested minutes when the
 * ticket is Due Out risk relevant (Phase 5.4), otherwise 0.
 */
export function getRolloverBayOccupancyImpact(
  ticket: NonBayHoldRolloverShape,
  context?: NonBayHoldRolloverContext,
): NonBayHoldRolloverVerdict {
  const blockers: NonBayHoldBlocker[] = [];
  const requestedMin = clampPositive(context?.requestedHoldMinutes);

  // 1. Missing ticket.
  if (ticket == null) {
    pushUnique(blockers, 'missing_bay_context');
    return {
      verdict: 'missing_location_context',
      blockers,
      bayBlockingMinutes: 0,
      dueOutRiskMinutes: 0,
      ok: false,
    };
  }

  // 2. Terminal.
  if (isTerminalForRollover(ticket)) {
    return {
      verdict: 'no_bay_occupancy_growth',
      blockers,
      bayBlockingMinutes: 0,
      dueOutRiskMinutes: 0,
      ok: true,
    };
  }

  const productionState = getProductionState(ticket);
  const dueOutExclusion = getDueOutRiskExclusionReason(ticket);
  const dueOutRiskRelevant = dueOutExclusion === null;
  if (!dueOutRiskRelevant) {
    pushUnique(blockers, 'missing_due_out_context');
  }

  // 3. Parking lot inactive — not active rollover work.
  if (productionState === 'parking_lot_inactive') {
    pushUnique(blockers, 'parking_lot_inactive_excluded');
    pushUnique(blockers, 'non_bay_hold_not_bay_blocking');
    return {
      verdict: 'parking_lot_inactive_no_active_rollover',
      blockers,
      bayBlockingMinutes: 0,
      dueOutRiskMinutes: 0,
      ok: true,
    };
  }

  // 4. Explicit bay-hold owner (Phase 2.1) or caller override.
  if (context?.treatAsExplicitBayHold === true) {
    return {
      verdict: 'bay_hold_owner_blocks_bay',
      blockers,
      bayBlockingMinutes: requestedMin,
      dueOutRiskMinutes: dueOutRiskRelevant ? requestedMin : 0,
      ok: true,
    };
  }
  if (
    ticket.bay_hold_owner != null &&
    isBayBlockingHoldOwner(ticket.bay_hold_owner)
  ) {
    return {
      verdict: 'bay_hold_owner_blocks_bay',
      blockers,
      bayBlockingMinutes: requestedMin,
      dueOutRiskMinutes: dueOutRiskRelevant ? requestedMin : 0,
      ok: true,
    };
  }
  if (ticket.is_explicit_bay_hold === true) {
    return {
      verdict: 'bay_hold_owner_blocks_bay',
      blockers,
      bayBlockingMinutes: requestedMin,
      dueOutRiskMinutes: dueOutRiskRelevant ? requestedMin : 0,
      ok: true,
    };
  }

  // 5. Held in bay.
  if (productionState === 'held_in_bay') {
    return {
      verdict: 'in_bay_pause_blocks_bay',
      blockers,
      bayBlockingMinutes: requestedMin,
      dueOutRiskMinutes: dueOutRiskRelevant ? requestedMin : 0,
      ok: true,
    };
  }

  // 6. Active in bay.
  if (productionState === 'active_in_bay') {
    return {
      verdict: 'bay_occupancy_growth_allowed',
      blockers,
      bayBlockingMinutes: requestedMin,
      dueOutRiskMinutes: dueOutRiskRelevant ? requestedMin : 0,
      ok: true,
    };
  }

  // 7. Held in parking — bay growth suppressed; Due Out still grows.
  if (productionState === 'held_in_parking') {
    pushUnique(blockers, 'held_in_parking_cannot_inflate_bay_occupancy');
    pushUnique(blockers, 'non_bay_hold_not_bay_blocking');
    if (dueOutRiskRelevant) pushUnique(blockers, 'due_out_risk_may_increase');
    return {
      verdict: 'held_in_parking_not_bay_blocking',
      blockers,
      bayBlockingMinutes: 0,
      dueOutRiskMinutes: dueOutRiskRelevant ? requestedMin : 0,
      ok: true,
    };
  }

  // 7b. Scheduled-but-parked — same suppression doctrine.
  if (productionState === 'scheduled_but_parked') {
    pushUnique(blockers, 'parking_location_suppresses_bay_occupancy');
    pushUnique(blockers, 'non_bay_hold_not_bay_blocking');
    if (dueOutRiskRelevant) pushUnique(blockers, 'due_out_risk_may_increase');
    return {
      verdict: 'held_in_parking_not_bay_blocking',
      blockers,
      bayBlockingMinutes: 0,
      dueOutRiskMinutes: dueOutRiskRelevant ? requestedMin : 0,
      ok: true,
    };
  }

  // 8. Physical location is parking but production state didn't
  //    resolve to held/scheduled-but-parked (e.g., parking spot with
  //    no scheduling). Treat as parking-suppressed.
  const loc = getTimerStartLocationState(ticket);
  if (loc === 'in_parking_spot' || loc === 'in_general_lot') {
    pushUnique(blockers, 'parking_location_suppresses_bay_occupancy');
    pushUnique(blockers, 'non_bay_hold_not_bay_blocking');
    if (dueOutRiskRelevant) pushUnique(blockers, 'due_out_risk_may_increase');
    return {
      verdict: 'due_out_risk_growth_only',
      blockers,
      bayBlockingMinutes: 0,
      dueOutRiskMinutes: dueOutRiskRelevant ? requestedMin : 0,
      ok: true,
    };
  }

  // 9. Estimate / not-yet-scheduled.
  if (productionState === 'estimate_prospect') {
    return {
      verdict: 'no_bay_occupancy_growth',
      blockers,
      bayBlockingMinutes: 0,
      dueOutRiskMinutes: 0,
      ok: true,
    };
  }

  // 10. Defensive fall-through.
  pushUnique(blockers, 'missing_bay_context');
  pushUnique(blockers, 'bay_id_required_for_bay_occupancy_growth');
  return {
    verdict: 'requires_manual_review',
    blockers,
    bayBlockingMinutes: 0,
    dueOutRiskMinutes: dueOutRiskRelevant ? requestedMin : 0,
    ok: false,
  };
}

// =============================================================================
// Convenience wrappers
// =============================================================================

/** Alias: get the canonical verdict for non-bay hold rollover. */
export function getNonBayHoldRolloverVerdict(
  ticket: NonBayHoldRolloverShape,
  context?: NonBayHoldRolloverContext,
): RolloverBayOccupancyVerdict {
  return getRolloverBayOccupancyImpact(ticket, context).verdict;
}

/** Flat blocker list. */
export function getRolloverBayOccupancyBlockers(
  ticket: NonBayHoldRolloverShape,
  context?: NonBayHoldRolloverContext,
): NonBayHoldBlocker[] {
  return getRolloverBayOccupancyImpact(ticket, context).blockers;
}

/**
 * Compute bay-blocking minutes that the caller may apply to next-day
 * bay occupancy. Returns 0 unless the ticket is in a bay-blocking
 * state.
 */
export function calculateBayBlockingRolloverMinutes(
  ticket: NonBayHoldRolloverShape,
  context?: NonBayHoldRolloverContext,
): number {
  return getRolloverBayOccupancyImpact(ticket, context).bayBlockingMinutes;
}

/**
 * Compute Due Out risk minutes that may grow while the ticket is on
 * hold. Independent of bay occupancy.
 *
 * Returns 0 when the ticket is not Due Out risk relevant
 * (Phase 5.4 exclusion) OR terminal. Otherwise returns the
 * caller's requested minutes.
 */
export function calculateDueOutRiskGrowthWhileHeld(
  ticket: NonBayHoldRolloverShape,
  context?: NonBayHoldRolloverContext,
): number {
  return getRolloverBayOccupancyImpact(ticket, context).dueOutRiskMinutes;
}

// =============================================================================
// Adoption points (documented in code; no behavior change in this slice)
// =============================================================================
//
// 1. **Runtime is ALREADY CORRECT** — no patch needed:
//    - `bayOccupancyEstimate.detectVehicleBayPresence` gates advisor
//      minutes via `bayId + !parkingLot`. Held-in-parking tickets
//      get `in_parking_lot` and the advisor delay is NOT attributed
//      to bay occupancy.
//    - `createEODRolloverSplit` creates Part B with `predicted_minutes: 1`
//      shadow ("grows via durable accumulation") — NOT inflated from
//      Part A's hold time. Bay placement uses `pickCompatibleBayForTech`
//      which filters lockouts (Phase 7.9) but does not take hold
//      time as input.
//    - Phase 5.4 `getDueOutRiskExclusionReason` returns `null` for
//      `held_in_parking` — Due Out risk stays active.
//    - Phase 2.1 `isBayBlockingHoldOwner` covers the explicit
//      bay-hold-owner exception (parts / vendor / sublet / machine).
//
// 2. **Phase 8.3 contribution**: canonical helper + verdict
//    vocabulary so future planners / cascade / UI can ask the
//    doctrinal question and get a single answer. Source-scan tests
//    pin the runtime correctness.
//
// 3. **Source-scan tests (Phase 8.3)** confirm that:
//    - `bayOccupancyEstimate.detectVehicleBayPresence` excludes
//      parking-lot tickets from `in_bay`.
//    - `eodRolloverHandler.createEODRolloverSplit` Part B uses
//      `predicted_minutes: 1` shadow, not Part A hold minutes.
//
// 4. **Future schema enrichment (Phase 9+ candidate)**: a future
//    `tickets.bay_blocking_hold_owner` enum column could persist
//    Phase 2.1 hold owner. Today the helper accepts it via input
//    context — adoption is non-breaking when the column lands.
//
// 5. **Phase 9+ regression suite**: this helper's verdict + blocker
//    vocabulary becomes part of the canonical regression battery
//    for "does non-bay hold inflate rollover bay occupancy?".
